import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Globe, Plus } from "lucide-react";

interface WorkflowEnvironmentsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workflowName: string;
  currentEnvironments: string[];
  availableEnvironments: string[];
  isSaving: boolean;
  onSave: (environments: string[]) => void;
}

export const WorkflowEnvironmentsDialog: React.FC<WorkflowEnvironmentsDialogProps> = ({
  open,
  onOpenChange,
  workflowName,
  currentEnvironments,
  availableEnvironments,
  isSaving,
  onSave,
}) => {
  const [selectedEnvs, setSelectedEnvs] = useState<Set<string>>(
    new Set(currentEnvironments.length > 0 ? currentEnvironments : ["production"])
  );
  const [customEnv, setCustomEnv] = useState("");

  // Reset state when dialog opens
  React.useEffect(() => {
    if (open) {
      setSelectedEnvs(
        new Set(currentEnvironments.length > 0 ? currentEnvironments : ["production"])
      );
      setCustomEnv("");
    }
  }, [open, currentEnvironments]);

  const allEnvs = Array.from(
    new Set([...availableEnvironments, ...selectedEnvs])
  ).sort();

  const toggleEnv = (env: string) => {
    setSelectedEnvs((prev) => {
      const next = new Set(prev);
      if (next.has(env)) next.delete(env);
      else next.add(env);
      return next;
    });
  };

  const addCustomEnv = () => {
    const env = customEnv.trim().toLowerCase();
    if (env && !selectedEnvs.has(env)) {
      setSelectedEnvs((prev) => new Set(prev).add(env));
    }
    setCustomEnv("");
  };

  const handleSave = () => {
    const envs = Array.from(selectedEnvs);
    if (envs.length > 0) {
      onSave(envs);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="light" className="sm:max-w-[400px] p-0">
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="flex items-center gap-2 text-card-foreground">
            <Globe className="h-4 w-4" />
            Run environments
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Select which environments <span className="font-medium text-card-foreground">{workflowName}</span> runs on.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 px-6 pb-4">
          <div className="space-y-1.5 max-h-40 overflow-y-auto border border-border rounded-md p-2">
            {allEnvs.map((env) => (
              <label
                key={env}
                className="flex items-center gap-2 text-sm text-card-foreground cursor-pointer hover:bg-muted/50 rounded px-1 py-0.5"
              >
                <input
                  type="checkbox"
                  checked={selectedEnvs.has(env)}
                  onChange={() => toggleEnv(env)}
                  className="rounded border-input accent-primary"
                />
                {env}
              </label>
            ))}
            {allEnvs.length === 0 && (
              <p className="text-xs text-muted-foreground">
                No environments found. Add one below.
              </p>
            )}
          </div>

          <div className="flex gap-1.5">
            <Input
              value={customEnv}
              onChange={(e) => setCustomEnv(e.target.value)}
              placeholder="Add custom environment"
              className="h-8 text-xs"
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addCustomEnv();
                }
              }}
            />
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2 border-border text-card-foreground hover:bg-muted"
              onClick={addCustomEnv}
              disabled={!customEnv.trim()}
            >
              <Plus className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-border bg-muted/30">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)} className="border-border text-card-foreground hover:bg-muted">
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={isSaving || selectedEnvs.size === 0}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
